import { useCallback, useContext, useEffect, useState } from "react";
import { genericEntityConstants } from "../../../api/GenericConstants";
import { ILockableEntity, ISignableEntity } from "../../../api/GenericTypes";
import { Button } from "../../../common/buttons/Button/Button";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { GenericEntity, GenericEntityComponentProps } from "../GenericComponents";
import { AlertModal, AlertModalOnProceedProps } from "../../../common/modals/AlertModal/AlertModal";
import { useEntityApi } from "../../../api/useEntityApi";
import { useQueryClient } from "@tanstack/react-query";
import { SessionContext } from "../../../common/contexts/SessionContext";
import { useSigninRedirectWithAction } from "../../../main/LoginPage/OidcRedirect";
import { getDetailLink } from "../../../main/Routing";
import { Signature } from "../../../api/Signature";
import { useForm } from "react-hook-form";
import { TextareaFormField } from "../../../common/formfields/TextareaFormField";
import styles from "../../../common/modals/AlertModal/AlertModal.module.css";
import { useEntityPermissions } from "../../../common/permissions/useEntityPermissions";

export interface GenericEntityUnlockButtonProps<Entity = GenericEntity> {
  btnClass?: string;
  hideLabel?: boolean;
  onSuccess?: (data: Entity) => void;
  overrideRedirectUrl?: (entity: Entity) => string;
  onClickCallback?(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void;
}

export const GenericEntityUnlockButton = <
  Entity extends GenericEntity & Partial<ILockableEntity> & Partial<ISignableEntity>,
  ComponentProps extends GenericEntityUnlockButtonProps<Entity>
>({
  entityTypeId,
  entity,
  componentProps,
}: GenericEntityComponentProps<Entity, ComponentProps>) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const { entityConstants } = genericEntityConstants[entityTypeId];
  const { unlockMutationAsync, isLoadingUnlockMutation } = useEntityApi<Entity>(entityConstants.resource);

  const queryClient = useQueryClient();

  const { route, session } = useContext(SessionContext);
  const isSSOSession = session?.type === "jwt";
  const isSigned = !!entity?.isSigned;
  const { handleReauth } = useSigninRedirectWithAction();

  const onSuccessCallback = useCallback(
    (data: Entity) => {
      componentProps?.onSuccess?.(data);
      queryClient.invalidateQueries({ queryKey: [entityConstants.resource] });
    },
    [componentProps, entityConstants.resource, queryClient]
  );

  const {
    register,
    formState: { errors },
    watch,
    reset,
  } = useForm<
    Partial<{
      signatureType: {
        id: Signature["signatureType"];
        label: Signature["signatureType"];
        value: Signature["signatureType"];
      };
      comment: string;
    }>
  >({});
  const currentSignatureType = watch("signatureType");
  const currentComment = watch("comment");

  const handleUnlock = useCallback(
    async (e, props?: AlertModalOnProceedProps) => {
      if (!entity?.id) return;
      else if (!!isSSOSession) {
        handleReauth({
          actions: ["Unlock"],
          entityIntIds: [entity.id],
          entityType: entityTypeId,
          redirectUrl:
            componentProps?.overrideRedirectUrl?.(entity) ??
            route(getDetailLink(entityConstants.frontendIndexRoute, entity.id)),
          signature: { comment: currentComment, signatureType: currentSignatureType?.value },
        });
      } else {
        await unlockMutationAsync(
          {
            id: entity.id,
            body: { credentials: props?.credentials, signature: { comment: currentComment } },
            showToast: true,
            entityName: entityConstants?.entitySingular,
          },
          { onSuccess: onSuccessCallback }
        ).catch((e) => console.error(e));
      }
      setShowModal(false);
    },
    [
      componentProps,
      currentComment,
      currentSignatureType?.value,
      entity,
      entityConstants?.entitySingular,
      entityConstants.frontendIndexRoute,
      entityTypeId,
      handleReauth,
      isSSOSession,
      onSuccessCallback,
      route,
      unlockMutationAsync,
    ]
  );

  useEffect(() => {
    if (!showModal) {
      reset();
    }
  }, [reset, showModal]);

  const { canUnlock } = useEntityPermissions({ entityTypeId });

  if (!entity || !entity.isLocked || !entityConstants) return null;
  return (
    <>
      <Button
        onClick={(e) => {
          setShowModal(true);
          componentProps?.onClickCallback?.(e);
        }}
        loading={isLoadingUnlockMutation}
        disabled={!canUnlock(entity)}
        className={componentProps?.btnClass ?? "btn btn-block btn-sm btn-ghost-info flex align-center gap-5"}
      >
        <LucideIcon name="lock-open" />
        {!componentProps?.hideLabel && "Unlock"}
      </Button>

      {showModal && (
        <AlertModal
          showModal={showModal}
          setShowModal={setShowModal}
          type="success"
          onProceed={handleUnlock}
          onCancel={() => setShowModal(false)}
          title="Unlock content"
          description={
            isSigned
              ? "Are you sure you want to unlock this content? This will remove all signatures and allow other users to edit this content."
              : "Are you sure you want to unlock this content? This will allow other users to edit this content."
          }
          proceedLabel="Unlock"
          iconOverride="lock-open"
          forceUserAuthentication={!isSSOSession}
          loading={isLoadingUnlockMutation}
          children={
            <form
              className={`form-horizontal ${styles.form_holder}`}
              style={{
                margin: 0,
                marginBottom: "5px",
                padding: "0 10px",
                border: "none",
                boxShadow: "none",
                textAlign: "left",
              }}
            >
              <TextareaFormField
                id="comment"
                label="Comment"
                errors={errors}
                register={register}
                horizontal
                placeholder="Enter comment..."
                autoFocus={!!isSSOSession}
              />
            </form>
          }
        />
      )}
    </>
  );
};
